import React from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import CardActions from '@material-ui/core/CardActions';
import TextField from '@material-ui/core/TextField';
import FileInput from '../../components/FileInput';

import SaveIcon from '@material-ui/icons/SaveAltTwoTone';
import DeleteIcon from '@material-ui/icons/DeleteForeverTwoTone';
import CloseIcon from '@material-ui/icons/CloseTwoTone';
import Divider from '@material-ui/core/Divider';
import Button from '../Button';
import { Map, List } from 'immutable';
import get from 'lodash/get';
import { useMutation } from 'react-apollo-hooks';
import { CREATE_PROMOTION } from '../../common/graphql/CreatePromotion';
import { UPDATE_PROMOTION } from '../../common/graphql/UpdatePromotion';
import { DELETE_PROMOTION } from '../../common/graphql/DeletePromotion';
import { GET_STORE_PROMOTIONS_BY_PERIOD } from '../../common/graphql/GetStorePromotionsByPeriod';
import { GET_PERIOD_PROMOTIONS } from '../../common/graphql/GetPeriodPromotions';
import Promise from 'bluebird';
import { Storage } from 'aws-amplify';
import UserContext from '../../contexts/UserContext';
import PhotoCatalog from '../PhotoCatalog';
import nanoid from 'nanoid';

const PromotionEditor = function(props) {
  const { classes, type, promotion, onClose, enableAttachments } = props;
  const [state, setState] = React.useState(Map({}));
  const [filePhotos, setFilePhotos] = React.useState([]);
  const [uploadPhotoUrls, setUploadPhotoUrls] = React.useState([]);
  const [uploadedPhotoUrls, setUploadedPhotoUrls] = React.useState([]);

  const { currentUser } = React.useContext(UserContext);

  React.useEffect(() => {
    setUploadPhotoUrls(
      filePhotos.map(photo => ({
        name: photo.name,
        url: URL.createObjectURL(photo),
        blob: true,
        new: true,
        key: nanoid()
      }))
    );
  }, [filePhotos]);

  const updateMode = !!promotion.id;
  const refetchQueries = () => [
    GET_STORE_PROMOTIONS_BY_PERIOD.key,
    GET_PERIOD_PROMOTIONS.key
  ];

  const savePromotion = useMutation(
    updateMode ? UPDATE_PROMOTION.mutation : CREATE_PROMOTION.mutation,
    {
      refetchQueries
    }
  );

  const deletePromotion = useMutation(DELETE_PROMOTION.mutation, {
    refetchQueries
  });

  const promotionType = get(promotion, 'type');
  const {
    isHotOffer
    // isMajorPromotion, isStorePromotion
  } = React.useMemo(() => {
    const typesList = [type, promotionType];
    const isHotOffer = typesList.includes('HOT_OFFER');
    const isMajorPromotion = typesList.includes('MAJOR_PROMOTION');
    const isStorePromotion = typesList.includes('STORE_PROMOTION');
    return { isHotOffer, isMajorPromotion, isStorePromotion };
  }, [promotionType, type]);

  React.useEffect(() => {
    if (!promotion) return;
    let stop = false;
    (async () => {
      const s3Files = await Promise.map(get(promotion, 'files') || [], async f => {
        const s3File = await Storage.get(f.filename);
        return {
          name: f.filename,
          url: s3File,
          key: nanoid()
        };
      });
      !stop && setUploadedPhotoUrls(s3Files);
    })();
    return () => {
      stop = true;
    };
  }, [promotion]);

  React.useEffect(() => {
    setState(oldState =>
      oldState.merge({
        ...promotion
      })
    );
  }, [promotion]);

  React.useEffect(() => {
    console.log('state.toJS()', state.toJS()); //TRACE
  }, [state]);

  React.useEffect(() => {
    if (isHotOffer) setState(oldState => oldState.set('storeType', 'CORE')); //<---- fix this
  }, [type, isHotOffer]);

  const allPhotoUrls = React.useMemo(() => {
    return [...uploadedPhotoUrls, ...uploadPhotoUrls];
  }, [uploadedPhotoUrls, uploadPhotoUrls]);

  const onSubmit = React.useCallback(async () => {
    setState(oldState => oldState.set('loading', true));

    const promotionPeriodId = get(promotion, 'promotionPeriodId');
    const promotionStoreId = get(promotion, 'promotionStoreId');
    const s3Files = await Promise.map(allPhotoUrls, async (photoFileUrl, ii) => {
      const { blob, name, url } = photoFileUrl;
      if (blob) {
        const blobObj = await fetch(url).then(r => r.blob());
        const fileName = `${promotionPeriodId}__${ii}__${name}`;
        const uploadedFile = await Storage.put(fileName, blobObj, {
          // level: 'protected',
          contentType: blobObj.type
        });
        return uploadedFile;
      }
      return {
        key: photoFileUrl.name
      };
    });
    const files = s3Files.map(s3f => ({
      userId: currentUser.username,
      filename: s3f.key,
      uploader: currentUser.username
    }));
    const input = {
      type,
      promotionPeriodId,
      promotionStoreId,
      name: state.get('name'),
      description:
        (state.get('description') || '').length > 0 ? state.get('description') : null,
      storeType: state.get('storeType'),
      files
    };
    if (updateMode) {
      input.id = promotion.id;
    }
    await savePromotion({
      variables: {
        input
      }
    });
    setState(oldState => oldState.set('loading', false));
    onClose && onClose();
  }, [
    state,
    promotion,
    allPhotoUrls,
    onClose,
    type,
    updateMode,
    savePromotion,
    currentUser.username
  ]);

  const onDelete = React.useCallback(async () => {
    setState(oldState => oldState.set('loading', true));
    await deletePromotion({
      variables: {
        input: {
          id: promotion.id
        }
      }
    });
    setState(oldState => oldState.set('loading', false));
  }, [promotion, deletePromotion]);

  const onCatalogItemDelete = React.useCallback(photoItem => {
    console.log('photoItem', photoItem); //TRACE
    const { new: isNew, key } = photoItem;
    const setterFn = isNew ? setUploadPhotoUrls : setUploadedPhotoUrls;
    setterFn(oldData => {
      const list = List(oldData);
      const toDeleteIdx = list.findIndex(i => i.key === key);
      return list.delete(toDeleteIdx);
    });
  }, []);

  const isLoading = state.get('loading', false);
  return (
    <Card data-testid="cmi-promotion-form" className={classes.card} elevation={0}>
      <Divider />
      <CardHeader subheader={(promotion.id ? 'Edit' : 'Create') + ' Promotion'} />
      <CardContent style={{ paddingTop: 0 }}>
        <TextField
          label="Name"
          fullWidth
          style={{ marginTop: 0 }}
          value={state.get('name', '')}
          name="name"
          onChange={e => {
            e.persist();
            setState(oldState =>
              oldState.merge({
                name: e.target.value
              })
            );
          }}
          margin="normal"
        />
        <div>
          <TextField
            label="Description"
            multiline
            name="description"
            fullWidth
            value={state.get('description') || ''}
            onChange={e => {
              e.persist();
              return setState(oldState =>
                oldState.set('description', get(e, 'target.value'))
              );
            }}
            margin="normal"
          />
        </div>
        {isHotOffer && (
          <div>
            <Typography variant="caption" gutterBottom>
              Store Type:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.get('storeType') === 'CORE'}
                  onChange={() => setState(oldState => oldState.set('storeType', 'CORE'))}
                  value="CORE"
                />
              }
              label="Core"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.get('storeType') === 'CORE_PLUS'}
                  onChange={() =>
                    setState(oldState => oldState.set('storeType', 'CORE_PLUS'))
                  }
                  value="CORE_PLUS"
                />
              }
              label="Core Plus"
            />
          </div>
        )}
        {enableAttachments && (
          <div>
            <Typography variant="caption" gutterBottom>
              Photos:
            </Typography>
            <PhotoCatalog
              photoUrls={allPhotoUrls}
              editable
              onItemDelete={onCatalogItemDelete}
            />
            <FileInput
              label="Upload Photos"
              accept="image/gif, image/jpg, image/jpeg, image/png"
              multiple
              onSelect={files => {
                console.log('files', files); //TRACE
                setFilePhotos(files);
              }}
            />{' '}
          </div>
        )}
      </CardContent>
      <CardActions>
        <Button color="primary" onClick={onSubmit} disabled={isLoading}>
          <SaveIcon />
          Save
        </Button>
        <Button onClick={() => onClose && onClose()} disabled={isLoading}>
          <CloseIcon />
          Cancel
        </Button>
        {updateMode && (
          <Button onClick={onDelete} color="secondary" disabled={isLoading}>
            <DeleteIcon />
            Delete
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default PromotionEditor;
